import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import { Modal } from "./Modal";
import { HeaderModal } from "./Header";
import { FooterModal } from "./Footer";
import { Body } from "./Body";
import { createStripeCheckoutSession } from "../../../services/billing";
import close from "assets/images/close-icon.svg";

interface AddCreditModalProps {
  isShow: boolean;
  setIsShow: Dispatch<SetStateAction<boolean>>;
  teamId: string;
  onSuccess: () => void;
}

const AddCreditModal: React.FC<AddCreditModalProps> = ({
  isShow,
  setIsShow,
  teamId,
  onSuccess,
}) => {
  const [amount, setAmount] = useState<number>(100);
  const [amountInput, setAmountInput] = useState<string>("100");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Reset amount input when modal opens
  useEffect(() => {
    if (isShow) {
      setAmount(100);
      setAmountInput("100");
      setError(null);
    }
  }, [isShow]);

  const onCloseModal = () => {
    setIsShow(false);
  };

  const handleSubmit = async () => {
    if (amount <= 0 || isNaN(amount)) {
      setError("Please enter a valid amount greater than 0");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // For Stripe payment, create checkout session and redirect
      const successUrl = `${window.location.origin}/settings?section=billing`;
      const cancelUrl = `${window.location.origin}/settings?section=billing`;

      const response: any = await createStripeCheckoutSession(
        teamId,
        amount,
        successUrl,
        cancelUrl
      );

      // Redirect to Stripe checkout
      if (
        response &&
        response.success &&
        response.data &&
        response.data.checkoutUrl
      ) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (err: any) {
      console.error("Error creating checkout session:", err);
      setError(err.message || "Failed to process payment. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal className={"modal-wrapper max-w-[480px]"} isShow={isShow}>
        <div className="modal-form">
          <HeaderModal
            title={"test"}
            isShowHeader={true}
            className="modal-header pt-6 px-6"
          >
            <div className="flex items-center justify-between pb-5">
              <div className="h-10 w-10 flex items-center justify-center rounded-full bg-blue-100">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z"
                    stroke="#1751D0"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 10H23"
                    stroke="#1751D0"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <img
                className="modal-close modal-toggle cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1 mb-4"
                id="modal-toggle"
                src={close}
                onClick={onCloseModal}
                alt="close icon"
              />
            </div>

            <h2 className="text-base text-[#0A090B] font-interSemiBold pb-1">
              Add Credits
            </h2>
            <p className="text-sm text-[#4F4D55] font-interRegular">
              Add credits to your account to use for services.
            </p>
          </HeaderModal>

          <Body title="" isShowBody={true} className="modal-body px-6 pt-5">
            <div className="mb-4">
              <label
                htmlFor="amount"
                className="text-[#2D2B32] text-sm font-interMedium block pb-2"
              >
                Amount
              </label>
              <div className="flex items-center border border-[#D0D5DD] hover:border-[#1751D0] focus-within:border-[#113B98] rounded-md overflow-hidden">
                <div className="px-2 text-[#667085] flex items-center h-full">
                  $
                </div>
                <input
                  name="amount"
                  value={amountInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers
                    if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
                      setAmountInput(value);
                      setAmount(parseFloat(value) || 0);

                      // Clear error if amount is valid
                      if (parseFloat(value) > 0) {
                        setError(null);
                      }
                    }
                  }}
                  onBlur={() => {
                    // Format on blur
                    if (amount > 0) {
                      setAmountInput(amount.toString());
                    } else {
                      setError("Amount must be greater than 0");
                    }
                  }}
                  className="placeholder:text-[#7F7D83] placeholder:text-[15px] text-base text-[#344054] w-full py-2 outline-none border-none"
                  placeholder="Enter amount"
                  type="text"
                />
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="mt-6 flex items-center p-3 border rounded-lg border-[#1751D0] bg-[#F5F8FF]">
              <div className="mr-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 4H3C1.89543 4 1 4.89543 1 6V18C1 19.1046 1.89543 20 3 20H21C22.1046 20 23 19.1046 23 18V6C23 4.89543 22.1046 4 21 4Z"
                    stroke="#1751D0"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 10H23"
                    stroke="#1751D0"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-interMedium text-[#344054]">
                  Secure Payment with Stripe
                </p>
                <p className="text-xs text-[#475467]">
                  You'll be redirected to Stripe's secure checkout page
                </p>
              </div>
            </div>

            <div className="mt-6 bg-[#F9FAFB] p-3 rounded-lg">
              <p className="text-sm text-[#475467]">
                After successful payment, credits will be added to your account
                immediately.
              </p>
            </div>
          </Body>

          <FooterModal
            title=""
            isShowFooter={true}
            className="modal-footer pt-5 px-6 pb-6"
          >
            <div className="flex items-center gap-x-3 justify-center">
              <button
                type="button"
                onClick={onCloseModal}
                className="text-[#4F4D55] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-2.5 !text-base flex-1 hover:bg-[#f2f4f7]"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className={`!text-base text-white font-interSemiBold bg-[#1751D0] hover:bg-[#113B98] rounded-lg px-3.5 py-2.5 flex-1 ${
                  isSubmitting ? "button-disabled" : ""
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add Credits"}
              </button>
            </div>
          </FooterModal>
        </div>
      </Modal>
    </>
  );
};

export default AddCreditModal;
